import { z } from 'zod';
import { MAX_NAME_LENGTH } from './common.js';
const uuidSchema = z.string().uuid();
const durationUnitEnum = z.enum([
    'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year',
]);
// ---------------------------------------------------------------------------
// Projects (Bryntum ProjectModel extensions)
// ---------------------------------------------------------------------------
export const projectScheduleUpdateSchema = z.object({
    start_date: z.string().datetime().optional().nullable(),
    end_date: z.string().datetime().optional().nullable(),
    calendar_id: uuidSchema.optional().nullable(),
    hours_per_day: z.number().min(0.1).max(24).optional(),
    days_per_week: z.number().min(0.1).max(7).optional(),
    days_per_month: z.number().min(0.1).max(31).optional(),
    direction: z.enum(['Forward', 'Backward']).optional(),
    status_id: uuidSchema.optional().nullable(),
});
// ---------------------------------------------------------------------------
// Calendars
// ---------------------------------------------------------------------------
export const projectCalendarCreateSchema = z.object({
    name: z.string().trim().min(1).max(MAX_NAME_LENGTH),
    project_id: uuidSchema.optional().nullable(),
    is_shared: z.boolean().optional(),
    parent_id: uuidSchema.optional().nullable(),
    unspecified_time_is_working: z.boolean().optional(),
});
export const projectCalendarUpdateSchema = projectCalendarCreateSchema.partial();
// ---------------------------------------------------------------------------
// Calendar intervals
// ---------------------------------------------------------------------------
export const projectCalendarIntervalCreateSchema = z.object({
    calendar_id: uuidSchema,
    name: z.string().max(MAX_NAME_LENGTH).optional().nullable(),
    start_date: z.string().datetime().optional().nullable(),
    end_date: z.string().datetime().optional().nullable(),
    recurrent_start_date: z.string().optional().nullable(),
    recurrent_end_date: z.string().optional().nullable(),
    is_working: z.boolean().optional(),
});
export const projectCalendarIntervalUpdateSchema = projectCalendarIntervalCreateSchema.partial();
// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------
export const projectResourceCreateSchema = z.object({
    project_id: uuidSchema,
    name: z.string().trim().min(1).max(MAX_NAME_LENGTH),
    calendar_id: uuidSchema.optional().nullable(),
    image: z.string().url().optional().nullable(),
    event_color: z.string().max(50).optional().nullable(),
    read_only: z.boolean().optional(),
    profile_id: uuidSchema.optional().nullable(),
    resource_type: z.enum(['person', 'crew', 'equipment', 'vehicle', 'facility', 'other']).optional(),
    email: z.string().email().optional().nullable(),
    phone: z.string().max(50).optional().nullable(),
    is_active: z.boolean().optional(),
    max_units: z.number().min(0).max(100).optional(),
});
export const projectResourceUpdateSchema = projectResourceCreateSchema.partial();
// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------
export const projectEventCreateSchema = z.object({
    project_id: uuidSchema,
    name: z.string().trim().min(1).max(MAX_NAME_LENGTH),
    start_date: z.string().datetime().optional().nullable(),
    end_date: z.string().datetime().optional().nullable(),
    duration: z.number().min(0).optional().nullable(),
    duration_unit: durationUnitEnum.optional(),
    effort: z.number().min(0).optional().nullable(),
    effort_unit: durationUnitEnum.optional(),
    effort_driven: z.boolean().optional(),
    scheduling_mode: z.enum(['Normal', 'FixedDuration', 'FixedUnits', 'FixedEffort']).optional(),
    manually_scheduled: z.boolean().optional(),
    inactive: z.boolean().optional(),
    constraint_type: z.enum([
        'muststarton', 'mustfinishon',
        'startnoearlierthan', 'startnolaterthan',
        'finishnoearlierthan', 'finishnolaterthan',
    ]).optional().nullable(),
    constraint_date: z.string().datetime().optional().nullable(),
    percent_done: z.number().min(0).max(100).optional(),
    event_color: z.string().max(50).optional().nullable(),
    event_style: z.string().max(50).optional().nullable(),
    icon_cls: z.string().max(100).optional().nullable(),
    cls: z.string().max(100).optional().nullable(),
    milestone_width: z.number().min(0).optional().nullable(),
    calendar_id: uuidSchema.optional().nullable(),
    ignore_resource_calendar: z.boolean().optional(),
    parent_id: uuidSchema.optional().nullable(),
    parent_index: z.number().int().min(0).optional(),
    rollup: z.boolean().optional(),
    resizable: z.string().optional(),
    draggable: z.boolean().optional(),
    planned_start_date: z.string().datetime().optional().nullable(),
    planned_end_date: z.string().datetime().optional().nullable(),
    planned_duration: z.number().min(0).optional().nullable(),
    planned_effort: z.number().min(0).optional().nullable(),
    event_type: z.string().max(50).optional().nullable(),
    notes: z.string().optional().nullable(),
    priority: z.enum(['low', 'normal', 'high', 'critical']).optional(),
});
export const projectEventUpdateSchema = projectEventCreateSchema.partial();
// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------
export const projectAssignmentCreateSchema = z.object({
    project_id: uuidSchema,
    event_id: uuidSchema,
    resource_id: uuidSchema,
    units: z.number().min(0).max(100).optional(),
});
export const projectAssignmentUpdateSchema = projectAssignmentCreateSchema.partial();
// ---------------------------------------------------------------------------
// Dependencies
// ---------------------------------------------------------------------------
export const projectDependencyCreateSchema = z.object({
    project_id: uuidSchema,
    from_event: uuidSchema,
    to_event: uuidSchema,
    type: z.number().int().min(0).max(3).optional(),
    lag: z.number().optional(),
    lag_unit: durationUnitEnum.optional(),
    cls: z.string().max(100).optional().nullable(),
    active: z.boolean().optional(),
    from_side: z.enum(['top', 'bottom', 'left', 'right']).optional().nullable(),
    to_side: z.enum(['top', 'bottom', 'left', 'right']).optional().nullable(),
});
export const projectDependencyUpdateSchema = projectDependencyCreateSchema.partial();
// ---------------------------------------------------------------------------
// Time ranges
// ---------------------------------------------------------------------------
export const projectTimeRangeCreateSchema = z.object({
    project_id: uuidSchema,
    name: z.string().trim().min(1).max(MAX_NAME_LENGTH),
    start_date: z.string().datetime(),
    end_date: z.string().datetime().optional().nullable(),
    duration: z.number().min(0).optional().nullable(),
    duration_unit: durationUnitEnum.optional(),
    cls: z.string().max(100).optional().nullable(),
    icon_cls: z.string().max(100).optional().nullable(),
    style: z.string().optional().nullable(),
    recurrence_rule: z.string().optional().nullable(),
    range_type: z.enum([
        'milestone', 'deadline', 'contract_period', 'regulatory', 'shutdown', 'other',
    ]).optional(),
});
export const projectTimeRangeUpdateSchema = projectTimeRangeCreateSchema.partial();
// ---------------------------------------------------------------------------
// Resource time ranges
// ---------------------------------------------------------------------------
export const projectResourceTimeRangeCreateSchema = z.object({
    project_id: uuidSchema,
    resource_id: uuidSchema,
    name: z.string().trim().min(1).max(MAX_NAME_LENGTH),
    start_date: z.string().datetime(),
    end_date: z.string().datetime().optional().nullable(),
    duration: z.number().min(0).optional().nullable(),
    duration_unit: durationUnitEnum.optional(),
    time_range_color: z.string().max(50).optional().nullable(),
    icon_cls: z.string().max(100).optional().nullable(),
    cls: z.string().max(100).optional().nullable(),
    style: z.string().optional().nullable(),
    range_type: z.enum([
        'unavailable', 'pto', 'maintenance', 'on_site', 'travel', 'training', 'other',
    ]).optional(),
});
export const projectResourceTimeRangeUpdateSchema = projectResourceTimeRangeCreateSchema.partial();
//# sourceMappingURL=projectsScheduleSchema.js.map